<template>
  <div class="pthvAroundMe-widget blockPthv">
    <div class="blockPthv-search-wrapper">
      <span v-if="isLoading">{{ $t("loading") }}</span>
      <ApthSearch
        v-else
        id="pthv-search"
        :items="stops"
        :lines="lines"
        :showCities="true"
        :selectedStop="stops[selectedStop]"
        :showStopName="showStopName"
        :showStopId="showStopId"
        :geoloc="enableGeolocUser"
        :showSubmitButton="showSubmitButton"
        @search="onSearch"
        @geoloc-user="geolocUser"
      />
      <ApthScheduleListing
        v-if="listingLoaded"
        id="pthv-listing"
        :siteBasePath="siteBasePath"
        :allDirections="true"
        :destinations="destinations"
        @lineSelected="listingSelectLine"
        :enableLinesLinks="enableLinesLinks"
        :showOccupancy="showOccupancy"
        :showDeparture="showDeparture"
        :showTerminus="showTerminus"
        :alwaysShowHours="alwaysShowHours"
      />
      <div v-else class="loader"></div>
      <ApthPoiTypes
        v-if="poisEnabled"
        id="pthv-poi-types"
        :network="network"
        :siteBasePath="siteBasePath"
        @selectpoitype="mapSelectPoiType"
        @poitypesready="mapSetAllPoisTypes"
      />
    </div>
    <div class="blockPthv-map-wrapper">
      <ApthMap
        id="pthv-map"
        :attribution="themeAttribution"
        :url="themeUrl"
        :siteBasePath="siteBasePath"
        :network="network"
        :showStopId="showStopId"
        :showBearing="showBearing"
        :stop="stop"
        :stopIdToOpen="stopIdToOpen"
        :line="line"
        :stops="stops"
        :buses="buses"
        :pois="pois"
        :allPoisTypes="allPoisTypes"
        :lines="lines"
        :shapes="shapes"
        :shapesLoading="shapesLoading"
        :assetspath="assetspath"
        :geolocUserTimestamp="geolocUserTimestamp"
        @selectstop="mapSelectStop"
        @selectline="mapSelectLine"
      />
    </div>
  </div>
</template>

<style>
.pthvAroundMe-widget {
  display: grid;
  row-gap: 2em;
  column-gap: 2em;
}
@media (min-width: 768px) {
  .pthvAroundMe-widget {
    grid-template-columns: 22em 1fr;
  }
}
#pthv-search,
#pthv-listing,
#pthv-map {
  width: 100%;
}
#pthv-map {
  min-height: 60vh;
}
@media (min-width: 768px) {
  #pthv-map {
    height: auto;
    min-height: 80vh;
  }
}
.blockPthv-search-wrapper {
  order: 1;
}
@media (min-width: 768px) {
  .blockPthv-search-wrapper {
    order: initial;
  }
}
.blockPthv-map-wrapper {
  z-index: 0;
}
.pthv-poi-types-wrapper {
  margin-block-start: 2rem;
}
.loader {
  width: 4rem;
  height: 4rem;
  background: url("../assets/loader.svg") no-repeat;
  background-size: contain;
}
</style>

<script>
//Components
import ApthSearch from "./ApthSearch";
import ApthScheduleListing from "./ApthScheduleListing";
import ApthPoiTypes from "./ApthPoiTypes";
import ApthMap from "./ApthMap";

// Support functions
// import initData from "../lib/initData.ts";
import { clearExpiredCacheStorage } from "../lib/utils.ts";
import initData from "../lib/initDataProto.ts";
import departuresFromGtfs from "../lib/departuresFromGtfs.ts";
import getLineShapes from "../lib/getLineShapes.ts";
import getBusesPositions from "../lib/getBusesPositions.ts";
import getPois from "../lib/poi/getPois.ts";

// Assets
import loader from '../assets/loader.svg';

// Mixins
import ParentMapBaseMixin from '../mixins/maps/ParentMapBaseMixin';

export default {
  name: "ApthAroundMeWidget",
  mixins: [ParentMapBaseMixin],
  created() {
    this.isLoading = true;
    clearExpiredCacheStorage(this.gtfsUri, this.encodeUriPath, this.network).then((expirationData) => {
        initData(this.gtfsUri, this.encodeUriPath, this.network, this.area, this.onlyPhysicals).then(
          (data) => {
            this.lines = data.lines;
            this.stops = data.stops;
            this.setFirstStop(data);
            this.isLoading = false;
          }
        );
      }
    );
    // TODO continuer de charger le reste des données en background
  },
  mounted() {
    // Mise à jour des positions de bus t
    if (this.updateBusPositionDelay > 0) {
      let delay = this.updateBusPositionDelay * 1000;
      setInterval(() => this.updateBusPosition(), delay);
    }
  },
  computed: {
    stopUri() {
      return (
        this.gtfsUri +
        "/stop-stoptimes-discovery?nbtimes=2&stop=" +
        this.selectedStop +
        "&reseau=" +
        this.network
      );
    },
    poisEnabled() {
      return this.siteBasePath === "" ? false : true;
    },
    selectdStopChilds() {
      let childs = this.selectedStop == "" ? [] : [this.selectedStop];
      // Si arret commercial, on requête sur les enfants
      let stop = this.stops[this.selectedStop];
      if (stop && stop.spoints && stop.spoints.length > 0) {
        childs = stop.spoints.map((p) => p.ref);
      }
      return childs;
    },
  },
  data() {
    return {
      //Default stop
      stop: {
        lat: 47.465978,
        lng: -0.559352,
      },
      currentArea: null,
      line: null,
      stops: {},
      buses: [],
      lines: {},
      pois: {},
      allPoisTypes: [],
      shapes: [],
      selectedStop: "",
      stopIdToOpen: null,
      destinations: [],
      geolocUserTimestamp: null,
      listingLoaded: true,
      shapesLoading: false,
      departuresFromGtfsOptions: {
        limit: this.numberSchedules,
        nbpasttimes: this.numberPastSchedules,
        pastsince: this.pastSchedulesSince,
        simulateOccupancy: this.simulateOccupancy,
      },
    };
  },
  components: {
    ApthSearch,
    ApthScheduleListing,
    ApthMap,
    ApthPoiTypes,
  },
  props: {
    gtfsUri: {
      type: String,
      required: true,
    },
    siteBasePath: {
      type: String,
      required: false,
      default: "",
    },
    encodeUriPath: {
      type: Boolean,
      required: false,
      default: false,
    },
    numberSchedules: {
      type: Number,
      required: false,
      default: 2,
    },
    numberPastSchedules: {
      type: Number,
      required: false,
      default: 2,
    },
    pastSchedulesSince: {
      type: Number,
      required: false,
      default: 480,
    },
    showStopName: {
      type: Boolean,
      required: false,
      default: true,
    },
    showStopId: {
      type: Boolean,
      required: false,
      default: false,
    },
    showBearing: {
      type: Boolean,
      required: false,
      default: true,
    },
    showDeparture: {
      type: Boolean,
      required: false,
      default: true,
    },
    showTerminus: {
      type: Boolean,
      required: false,
      default: true,
    },
    showOccupancy: {
      type: Boolean,
      required: false,
      default: true,
    },
    simulateOccupancy: {
      type: Boolean,
      required: false,
      default: false,
    },
    updateBusPositionDelay: {
      type: Number,
      required: false,
      default: 0,
    },
    // allTrips : affiche tous les trajets de la ligne, y compris ceux qui ne passent pas par l'arrêt
    allTrips: {
      type: Boolean,
      required: false,
      default: false,
    },
    network: {
      type: String,
      required: true,
    },
    area: {
      type: String,
      required: false,
    },
    assetspath: {
      type: String,
      required: false,
      default: () => {
        return "";
      },
    },
    alwaysShowHours: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableGeolocUser: {
      type: Boolean,
      required: false,
      default: true,
    },
    enableLinesLinks: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyPhysicals: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSubmitButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  methods: {
    onSearch(search) {
      // this.stop = { lat: line.lat, lng: line.lng };
      this.selectedStop = search.stopref;
      let selStop = this.stops[search.stopref];
      if (selStop.x) {
        this.stop = {
          lat: selStop.x,
          lng: selStop.y,
        };
        // We store the stopId to use it as the center of the map for the next load
        this.setLastStopStorage(selStop);
      }
      this.getDeparturesFromGtfs(search.stopref);
    },
    // Set first stop and the center on map loading
    setFirstStop(data) {
      // Stop stored in localStorage case
      let stopStored = this.getLastStopStorage();
      let stop = undefined;
      if (stopStored) {
        stop = JSON.parse(stopStored);
        // If this stop no longer exists, don't use this data
        if (data.stops[stop.ref]) {
          this.stop = {
            lat: data.stops[stop.ref].x,
            lng: data.stops[stop.ref].y,
          };
          this.getDeparturesFromGtfs(stop.ref);
        }
      }
      // No stop stored in localStorage
      if (!stopStored || !data.stops[stop.ref]) {
        // Default stop : the first stop with at least one line
        let stopIds = Object.getOwnPropertyNames(data.stops);
        if (stopIds.length > 0) {
          let stop = Object.values(data.stops).find(function (el) {
            return el.lines.length > 0;
          });
          // If we have no stop with lines, get first stop
          stop = stop || data.stops[stopIds[0]];
          this.stop = {
            lat: stop.x,
            lng: stop.y,
          };
          this.getDeparturesFromGtfs(stop.ref);
        }
      }
    },
    getDeparturesFromGtfs(stopId) {
      if (this.stops[stopId]) {
        departuresFromGtfs(
          this.gtfsUri,
          this.encodeUriPath,
          this.network,
          [this.stops[stopId]],
          this.lines,
          this.departuresFromGtfsOptions,
        ).then(res => {
          this.destinations = res;
          this.stopIdToOpen = stopId;
        });
      }
    },
    listingSelectLine(destination) {
      this.line = destination.line.ref;
      this.mapSelectLine(destination.line.ref, "");
    },
    // Get pois data for all pois types selected
    mapSelectPoiType(poiTypes) {
      this.pois = {};
      poiTypes.forEach((poiId) => {
        getPois(this.siteBasePath, this.network, poiId).then((data) => {
          this.pois[poiId] = data
        });
      });
    },
    // Set all pois types data
    mapSetAllPoisTypes(allPoisTypes) {
      this.allPoisTypes = allPoisTypes;
    },
    mapSelectStop(stop) {
      this.listingLoaded = false;
      this.line = null;
      this.buses = [];
      this.selectedStop = stop.parentRef;
      this.stop = stop.latlng;
      if (!(stop.parentRef in this.stops)) {
        // console.error(stop.parentRef, "not found");
      } else {
        // We store the stopId to use it as the center of the map for the next load
        this.setLastStopStorage(stop);
        departuresFromGtfs(
          this.gtfsUri,
          this.encodeUriPath,
          this.network,
          [this.stops[stop.parentRef]],
          this.lines,
          this.departuresFromGtfsOptions,
        ).then(res => {
          this.destinations = res;
          this.listingLoaded = true;
        });

      }
    },
    mapSelectLine(line, stopId) {
      if (typeof stopId === "undefined" || stopId == '') {
        stopId = this.selectedStop ? this.selectedStop : '';
      }
      this.shapesLoading = true;
      getLineShapes(
        this.gtfsUri,
        this.encodeUriPath,
        this.network,
        line,
        this.allTrips ? "" : stopId
      ).then((res) => {
        this.shapes = res.shapes;
        this.shapesLoading = false;
      });

      //this.line = this.lines[line];
      this.line = line;
      let selStop = this.stops[stopId];
      if (selStop && selStop.x) {
        this.stop = {
          lat: selStop.x,
          lng: selStop.y,
        };
      }
      if (this.displayVehiculePosition && this.updateBusPositionDelay != 0) {
        this.updateBusPosition();
      }
    },
    updateBusPosition(){
      if (this.line){
        let lineColor = "FFF";
        let lineMnemo = "";
        let line = this.lines[this.line];
        if (line){
          lineColor = line.color;
          lineMnemo = line.mnemo;
        }
        // @see ParentBaseMixin.js
        let vehiculeType = this.getVehiculeType(this.line);
        getBusesPositions(
          this.gtfsUri,
          this.encodeUriPath,
          this.network,
          this.line,
          undefined, // tripId non accessible ici
          this.allTrips ? "" : this.selectdStopChilds.join("|")
        ).then(res => {
          this.buses = res.map(t => ({
            x: t.latitude,
            y: t.longitude,
            bearing: t.bearing,
            headsign: t.tripHeadsign,
            line: this.line,
            color: lineColor,
            mnemo: lineMnemo,
            type: vehiculeType,
          }));
        });
      }
    },
    geolocUser() {
      this.geolocUserTimestamp = Date.now();
    },
  },
};
</script>
